/**
 * Subscription row resolution — how a set of `subscriptions` rows for one
 * organization collapses into "the subscription" and, from there, into a plan.
 *
 * An org legitimately owns more than one row. Better Auth's Stripe plugin
 * inserts an `incomplete` row before every Checkout redirect (abandoning the
 * redirect leaves it behind) and its `customer.subscription.created` handler
 * inserts a fresh row whenever it cannot find one by `stripeSubscriptionId` —
 * the path a subscription created straight from the Stripe dashboard takes. A
 * unique constraint on `referenceId` would make those inserts fail, so the
 * readers are what have to choose, and they have to choose the same way on
 * both sides of the wire.
 */

import { PLAN_RANK, type PlanId } from "./plans";

/**
 * Subscription statuses that grant a paid plan's entitlements. A trialing org
 * gets its trial plan's limits (this is how "trial limits" are enforced); once
 * the trial lapses (`canceled` / `unpaid` / etc.) the org falls back to the
 * implicit free plan.
 */
export const ENTITLED_STATUSES = ["trialing", "active", "past_due"] as const;

export type EntitledStatus = (typeof ENTITLED_STATUSES)[number];

/** True when a subscription status grants its plan's entitlements. */
export const isEntitledStatus = (
  status: string | null | undefined,
): status is EntitledStatus =>
  !!status && (ENTITLED_STATUSES as readonly string[]).includes(status);

/**
 * The subset of a `subscriptions` row this module reasons about. Structural so
 * the Drizzle row and the tRPC output both satisfy it without a cast.
 */
export interface SubscriptionRowLike {
  plan: string;
  status?: string | null;
  periodEnd?: Date | string | null;
}

/** Epoch millis for a row's period end; `-Infinity` when absent or unparseable. */
const periodEndTime = (row: SubscriptionRowLike): number => {
  if (!row.periodEnd) return Number.NEGATIVE_INFINITY;
  const time = new Date(row.periodEnd).getTime();
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
};

/** Tier of a row's plan, so a paid row outranks an unrecognized one. */
const planRank = (row: SubscriptionRowLike): number =>
  row.plan in PLAN_RANK ? PLAN_RANK[row.plan as PlanId] : 0;

/** Picks the higher-precedence of two rows: better tier first, then later period end. */
const preferred = <T extends SubscriptionRowLike>(a: T, b: T): T => {
  const rankDelta = planRank(b) - planRank(a);
  if (rankDelta !== 0) return rankDelta > 0 ? b : a;
  return periodEndTime(b) > periodEndTime(a) ? b : a;
};

/**
 * Collapses an org's subscription rows into the one that represents it.
 *
 * An entitled row always wins, so a stale `canceled` row can never mask a live
 * one. When nothing is entitled the most recent lapsed row is returned rather
 * than `null` — the sidebar trial badge renders its "Trial ended" state from
 * that row, and callers that only care about entitlements go through
 * `planFromSubscription`, which re-checks the status anyway.
 */
export const selectEffectiveSubscription = <T extends SubscriptionRowLike>(
  rows: readonly T[],
): T | null => {
  let entitled: T | null = null;
  let lapsed: T | null = null;

  for (const row of rows) {
    if (isEntitledStatus(row.status)) {
      entitled = entitled ? preferred(entitled, row) : row;
    } else {
      lapsed = lapsed ? preferred(lapsed, row) : row;
    }
  }

  return entitled ?? lapsed;
};

/**
 * The plan a subscription row entitles its org to. Anything that isn't a live
 * paid subscription resolves to `"free"` — the implicit default for orgs with
 * no subscription, a lapsed one, or a row on a plan this app doesn't know.
 */
export const planFromSubscription = (
  subscription: SubscriptionRowLike | null | undefined,
): PlanId => {
  if (!subscription || !isEntitledStatus(subscription.status)) return "free";
  const { plan } = subscription;
  return plan === "pro" || plan === "max" ? plan : "free";
};
